import * as _ from 'lodash';
import React from 'react';
import {useMemo, useRef} from 'react';
import {VisualizationSpec} from 'react-vega';
import CustomPanelRenderer from '../Vega3/CustomPanelRenderer';
import {useGatedValue, useOnScreen} from '../../state/hooks';
import * as Panel2 from './panel';
import * as CGReact from './cg/cgreact';
import * as String from '../../util/string';

const inputType = {
  type: 'dict' as const,
  objectType: {
    type: 'array' as const,
    objectType: 'string' as const,
  },
};

type PanelMultiStringHistogramProps = Panel2.PanelProps<typeof inputType>;

/* eslint-disable no-template-curly-in-string */

const HISTO_SPEC: VisualizationSpec = {
  $schema: 'https://vega.github.io/schema/vega/v5.json',
  description: 'A multi-run confusion matrix example.',
  autosize: 'fit-y',
  data: [
    {
      name: 'wandb',
      transform: [
        {
          type: 'filter',
          expr:
            "if(indexof(split(replace(trim(classesToFilter), regexp(/\\s+,\\s+|,\\s+|\\s+,/g), ','), ','), datum.${field:Actual})==-1 && filterClasses, false, true)",
        },
        {
          type: 'filter',
          expr:
            "if(indexof(split(replace(trim(classesToFilter), regexp(/\\s+,\\s+|,\\s+|\\s+/g), ','), ','), datum.${field:Predicted})==-1 && filterClasses, false, true)",
        },
      ],
    },
  ],
  scales: [
    {
      name: 'yscale',
      type: 'band',
      domain: {data: 'wandb', field: '${field:Actual}', sort: true},
      range: 'height',
      padding: 0.2,
    },
    {
      name: 'xscale',
      type: 'band',
      domain: {data: 'wandb', field: '${field:Predicted}', sort: true},
      range: 'width',
      round: true,
      padding: 0.2,
    },
    {
      name: 'colorScale',
      domain: {data: 'wandb', field: 'name', sort: true},
      type: 'ordinal',
      range: {data: 'wandb', field: 'color'},
    },
  ],

  signals: [
    {
      name: 'width',
      init: 'isFinite(containerSize()[0]) ? 0.98*containerSize()[0] : 200',
      on: [
        {
          update:
            'isFinite(containerSize()[0]) ? 0.98*containerSize()[0] : 200',
          events: 'window:resize',
        },
      ],
    },
    {
      name: 'height',
      init: 'isFinite(containerSize()[1]) ? 0.98*containerSize()[1] : 200',
      on: [
        {
          update:
            'isFinite(containerSize()[1]) ? 0.98*containerSize()[1] : 200',
          events: 'window:resize',
        },
      ],
    },
    {
      name: 'filterClasses',
      value: false,
      bind: {
        input: 'checkbox',
      },
    },
    {
      name: 'classesToFilter',
      description: 'A comma separated list of classes',
      value: '',
      bind: {
        input: 'input',
        placeholder: 'Comma separated classes to compare',
        size: 80,
      },
    },
    {
      name: 'selectedRun',
      value: '',
      on: [
        {
          events: [{type: 'click', marktype: 'rect'}],
          update: "if(selectedRun == datum.name, '', datum.name)",
        },
      ],
    },
    {
      name: 'suppressReturn',
      on: [
        {
          events: 'enterKey.keypress',
          update: 'false',
        },
      ],
    },
  ],
  axes: [
    {
      orient: 'left',
      scale: 'yscale',
      tickSize: 0,
      labelPadding: 0,
      domain: false,
      labelAlign: 'center',
      offset: {signal: '-0.01*width'},
      labelAngle: -90,
      title: 'Actual',
      labelOverlap: true,
    },
    {
      orient: 'top',
      scale: 'xscale',
      tickSize: 0,
      labelPadding: -5,
      domain: false,
      title: 'Predicted',
    },
  ],
  marks: [
    {
      type: 'group',
      from: {
        facet: {
          data: 'wandb',
          name: 'facet',
          groupby: ['${field:Predicted}'],
        },
      },

      encode: {
        update: {
          x: {scale: 'xscale', field: '${field:Predicted}'},
        },
      },
      signals: [{name: 'width', update: "bandwidth('xscale')"}],
      scales: [
        {
          name: 'cat2',
          type: 'linear',
          range: 'width',
          domain: {data: 'wandb', field: '${field:nPredicted}'},
          zero: true,
        },
        {
          name: 'catName',
          type: 'ordinal',
          range: 'height',
          domain: {data: 'wandb', field: 'name', sort: true},
        },
      ],
      axes: [
        {
          orient: 'bottom',
          scale: 'cat2',
          ticks: true,
          labels: true,
          grid: false,
          domain: true,
          offset: {signal: 'height'},
          labelOverlap: true,
        },
      ],
    },
    {
      type: 'group',
      from: {
        facet: {
          data: 'wandb',
          name: 'facet',
          groupby: ['${field:Actual}', '${field:Predicted}'],
        },
      },

      encode: {
        enter: {
          y: {scale: 'yscale', field: '${field:Actual}'},
          x: {scale: 'xscale', field: '${field:Predicted}'},
        },
        update: {
          y: {scale: 'yscale', field: '${field:Actual}'},
          x: {scale: 'xscale', field: '${field:Predicted}'},
        },
      },

      signals: [
        {name: 'height', update: "bandwidth('yscale')"},
        {name: 'width', update: "bandwidth('xscale')"},
      ],
      scales: [
        {
          name: 'cat1',
          type: 'band',
          range: 'height',
          domain: {data: 'facet', field: 'name', sort: true},
        },
        {
          name: 'cat2',
          type: 'linear',
          range: 'width',
          domain: {data: 'wandb', field: '${field:nPredicted}'},
          zero: true,
        },
      ],
      axes: [
        {
          orient: 'left',
          scale: 'cat1',
          ticks: true,
          labels: false,
          domain: true,
          grid: false,
        },
        {
          orient: 'left',
          offset: {signal: '-width'},
          ticks: false,
          scale: 'cat1',
          labels: false,
          domain: true,
          grid: false,
        },
        {
          orient: 'top',
          scale: 'cat2',
          ticks: false,
          labels: false,
          grid: true,
        },
        {
          orient: 'bottom',
          scale: 'cat2',
          ticks: false,
          labels: false,
          grid: false,
          domain: true,
          offset: {signal: 'height'},
        },
      ],
      marks: [
        {
          name: 'bars',
          from: {data: 'facet'},
          type: 'rect',
          encode: {
            update: {
              y: {scale: 'cat1', field: 'name'},
              height: {scale: 'cat1', band: 1.0},
              x2: {scale: 'cat2', value: 0},
              x: {scale: 'cat2', field: '${field:nPredicted}'},
              fill: {field: 'color'},
              opacity: [
                {
                  test:
                    "selectedRun == '' || (selectedRun != '' && datum.name == selectedRun)",
                  value: 1.0,
                },
                {value: 0.25},
              ],

              tooltip: [
                {
                  signal:
                    "{'Count': datum.${field:nPredicted}, 'Run name': datum.name}",
                },
              ],
            },
          },
        },
      ],
    },
  ],
  // TODO: get rid of as any, might need to upgrade vega types lib
} as any;

const PanelConfusionMatrix: React.FC<PanelMultiStringHistogramProps> = props => {
  const domRef = useRef<HTMLDivElement>(null);
  const onScreen = useOnScreen(domRef);
  const hasBeenOnScreen = useGatedValue(onScreen, o => o);
  const inputValueQuery = CGReact.useNodeValue(props.input.path);
  const inputValue = inputValueQuery.result;
  const keys = inputValue.map(kv => String.splitOnce(kv.key, '-')[1]!);
  const data = useMemo(() => {
    if (inputValueQuery.loading || keys.length < 2) {
      return [];
    }
    const classCounts: {[actual: string]: {[pred: string]: number}} = {};
    _.zip(inputValue[0].value, inputValue[1].value).forEach(
      ([actual, pred]) => {
        if (actual != null && pred != null) {
          if (classCounts[actual] == null) {
            classCounts[actual] = {};
          }
          if (classCounts[actual][pred] == null) {
            classCounts[actual][pred] = 0;
          }
          classCounts[actual][pred] += 1;
        }
      }
    );
    return _.flatMap(classCounts, (preds, actual) =>
      _.map(preds, (count, pred) => ({
        name: 'x',
        color: '#77aaaa',
        [keys[0]]: actual,
        [keys[1]]: pred,
        count,
      }))
    );
  }, [keys, inputValueQuery.loading, inputValue]);
  if (inputValueQuery.loading) {
    return <div>-</div>;
  }
  return (
    <div ref={domRef} style={{width: 800, height: 800}}>
      {hasBeenOnScreen ? (
        <CustomPanelRenderer
          spec={HISTO_SPEC}
          loading={props.loading}
          slow={false}
          data={data}
          userSettings={{
            fieldSettings: {
              Predicted: keys[0],
              Actual: keys[1],
              nPredicted: 'count',
            },
            stringSettings: {title: ''},
          }}
        />
      ) : (
        <div
          style={{
            backgroundColor: '#eee',
            margin: 16,
            width: '100%',
            height: '100%',
          }}
        />
      )}
    </div>
  );
};

export const Spec: Panel2.PanelSpec = {
  id: 'confusion-matrix',
  Component: PanelConfusionMatrix,
  inputType,
};
